import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { BuilderService } from 'src/app/builder-services/builder.service';
import { AppSettingsService } from '../services/app-settings.service';
import { ProductsService as standaloneProductsService } from '../../e-commerce/products/products.service';
import { CategoriesService as standaloneCategoriesService } from '../../e-commerce/categories/categories.service';
import { Router, NavigationEnd, Event } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { IntegrationsService } from '../../integrations/integrations.service';
import { ThemePalette } from '@angular/material/core'

class ImageSnippet {
  constructor(
    public src: string,
    public file: File
  ) {}
}
@Component({
  selector: 'content-settings',
  templateUrl: './content-settings.component.html',
  styleUrls: ['./content-settings.component.scss']
})
export class ContentSettingsComponent implements OnInit, OnDestroy {
  private currentUrl: string = ''
  private prevUrl: string = ''
  private routerSub: Subscription | undefined
  selectedSubCategories: any[] = [];
  settingsProgress = false;
  tokenCopied = false;
  public disabled = false
  public color: ThemePalette = 'primary'
  public touchUi = false
  selectedFile!: ImageSnippet

  contentSettingsForm = this.formBuilder.group({
    header_background: '',
    about_us_background: '',
    points_rewards_background: '',
    category_sub_page: '1',
    contact_email: '',
    contact_phone: '',
    contact_instagram: '',
    product_title_length: '',
    product_title_dots: '',
    mobile_webview_link: '',
    browser_type: 'appBrowser',
    stacks_extra_app_css: '',
    disable_guest: '1',
    enable_website_checkout: 'disabled',
    search_results_posts: '',
    landing_page: '/',
    selected_product: '',
    product_category: '',
    product_sub_category: '',
    wordpress_site_url: '',
    initial_order_status: ''
  })


  contentSettingsData: any = {}
  constructor(
    private formBuilder: FormBuilder,
    private appSettingsService: AppSettingsService,
    public builderService: BuilderService,
    public standaloneProductsService: standaloneProductsService,
    public standaloneCategoriesService: standaloneCategoriesService,
    private router: Router,
    private integrationsService: IntegrationsService,
  ) {}

  ngOnInit() {
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.prevUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      });
    this.getStandaloneSubCats();
    let project_id = this.builderService.getSelectedProject()
    this.contentSettingsData = this.appSettingsService.getContentSettings(project_id).subscribe(
      (settings: any) => {    
        this.contentSettingsData = settings !== null ? settings : {}
        if (settings !== null) {
          this.contentSettingsForm.patchValue({ category_sub_page: settings.category_sub_page || '1' })
          this.contentSettingsForm.patchValue({ disable_guest: settings.disable_guest || '1' })
          this.contentSettingsForm.patchValue({ enable_website_checkout: settings.enable_website_checkout })
          this.contentSettingsForm.patchValue({ contact_email: settings.contact_email || '' })
          this.contentSettingsForm.patchValue({ contact_phone: settings.contact_phone || '' })
          this.contentSettingsForm.patchValue({ contact_instagram: settings.contact_instagram || '' })
          this.contentSettingsForm.patchValue({ browser_type: settings.browser_type || 'appBrowser' })
          this.contentSettingsForm.patchValue({ landing_page: settings.landing_page || '/' })
          this.contentSettingsForm.patchValue({ selected_product: settings.selected_product })
          this.contentSettingsForm.patchValue({ product_category: settings.product_category })
          this.contentSettingsForm.patchValue({ product_sub_category: settings.product_sub_category })
          this.contentSettingsForm.patchValue({ wordpress_site_url: settings.wordpress_site_url })
          this.contentSettingsForm.patchValue({ initial_order_status: settings.initial_order_status })
          this.contentSettingsForm.patchValue({ mobile_webview_link: settings.mobile_webview_link || '' })

        }
      },
      (err) => {
        console.log(err)
      }
    )
  }

  toggleGuest(event: any) {
      const isChecked = event.target.checked;
      this.contentSettingsForm.get('disable_guest')?.setValue(isChecked ? '1' : 'disabled');
  }

  saveSettings(): void {
    this.settingsProgress = true;

    // Merge existing data with form data to avoid losing untracked fields (if any remain)
    // Though we are redesigning, we should be careful not to wipe 'initial_order_status' if it's still in the backend object but not in our form!
    // We already moved initial_order_status to CommerceOrders.

    let newSubmittedData: any = { ...this.contentSettingsData };
    const formValue = this.contentSettingsForm.value;

    // Update fields from form
    newSubmittedData['category_sub_page'] = formValue.category_sub_page;
    newSubmittedData['disable_guest'] = formValue.disable_guest;
    newSubmittedData['browser_type'] = formValue.browser_type || 'appBrowser';
    newSubmittedData['landing_page'] = formValue.landing_page;
    newSubmittedData['selected_product'] = formValue.selected_product;
    newSubmittedData['product_category'] = formValue.product_category;
    newSubmittedData['product_sub_category'] = formValue.product_sub_category;
    newSubmittedData['contact_email'] = formValue.contact_email;
    newSubmittedData['contact_phone'] = formValue.contact_phone;
    newSubmittedData['contact_instagram'] = formValue.contact_instagram;

    newSubmittedData['mobile_webview_link'] = formValue.mobile_webview_link || '';
    newSubmittedData['wordpress_site_url'] = formValue.wordpress_site_url || '';

    this.appSettingsService.setContentSettings(this.builderService.selectedProject, newSubmittedData).pipe(
      switchMap(() =>
        this.integrationsService.setIntegrations({
          wordpress_site_url: newSubmittedData['wordpress_site_url'],
        })
      )
    ).subscribe(
      (response) => {
        this.settingsProgress = false;
        // Update local data
        this.contentSettingsData = newSubmittedData;
      },
      (err) => {
        this.settingsProgress = false;
      }
    )
  }

  processFile(fileInput: any, type: string, name: string) {
    const file: File = fileInput.target.files[0]
    const reader = new FileReader()
    reader.addEventListener('load', (event: any) => {
      this.appSettingsService.uploadFile(file, this.builderService.selectedProject, type).subscribe(
        (res) => {
          this.contentSettingsData[name] = res
          this.builderService.showUpdateProgress = false
        },
        (err) => {
          this.builderService.showUpdateProgress = false
          alert('error uploading file, please contact support')
        }
      )
    })

    reader.readAsDataURL(file)
  }
  isProductsLandingPageSelected(): boolean {
    return this.contentSettingsForm.get('landing_page')?.value === 'products' && this.builderService.standalone
  }
  isCategoriesLandingPageSelected(): boolean {
    return this.contentSettingsForm.get('landing_page')?.value === 'productsCategories' || (this.contentSettingsForm.get('landing_page')?.value === 'productsSubCategories' && this.builderService.standalone)
  }
  isSubCategoriesLandingPageSelected(): boolean {
    const landingPage = this.contentSettingsForm.get('landing_page')?.value
    const categorySelected = this.contentSettingsForm.get('product_category')?.value
    return landingPage === 'productsSubCategories' && !!categorySelected && this.builderService.standalone
  }
  getStandaloneSubCats = () => {
    this.contentSettingsForm.get('product_category')?.valueChanges.subscribe((selectedCategoryId) => {
      const selectedCategory = this.standaloneCategoriesService.storedStandaloneCategories?.find((cat: any) => cat.id === selectedCategoryId)
      this.selectedSubCategories = selectedCategory?.subcategories || []

      this.contentSettingsForm.get('product_sub_category')?.setValue('')
    })
  }

  copyToken() {
    navigator.clipboard.writeText(this.builderService.getToken() ?? '')
    this.tokenCopied = true;
    setTimeout(() => this.tokenCopied = false, 2000);
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }
}
